import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/App';
import { useAuth } from '@/App';

/**
 * CreditsContext — global balance state + 402 interceptor.
 *
 * Usage:
 *   const { balance, pricing, priceOf, refresh, openBuyModal, BuyModalHost } = useCredits();
 *
 * The 402 interceptor is attached once on mount. Any Axios response with
 * status 402 from the backend triggers the buy-credits modal with the
 * deficit details pulled from `error.response.data.detail`.
 */
const CreditsContext = createContext(null);

export const useCredits = () => {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error('useCredits must be used within CreditsProvider');
  return ctx;
};

export function CreditsProvider({ children }) {
  const { user, token } = useAuth();
  const [balance, setBalance] = useState(null);
  const [pricing, setPricing] = useState({});
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [modalState, setModalState] = useState({ open: false, detail: null });
  const interceptorId = useRef(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const [balRes, priceRes] = await Promise.all([
        api.get('/billing/balance'),
        api.get('/billing/pricing'),
      ]);
      setBalance(balRes.data.credit_balance);
      setIsUnlimited(!!balRes.data.is_unlimited);
      setPricing(priceRes.data.pricing || {});
    } catch (_) {
      /* ignore — not fatal */
    }
  }, [token]);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  // Keep balance + pricing fresh without polling on every render (60 s).
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => { refresh(); }, 60_000);
    return () => clearInterval(id);
  }, [user, refresh]);

  // 402 interceptor — open the modal with backend-supplied deficit data.
  useEffect(() => {
    if (interceptorId.current !== null) return;
    interceptorId.current = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) {
          const detail = err.response.data?.detail || {};
          setModalState({ open: true, detail });
        }
        return Promise.reject(err);
      }
    );
    return () => {
      if (interceptorId.current !== null) {
        api.interceptors.response.eject(interceptorId.current);
        interceptorId.current = null;
      }
    };
  }, []);

  const priceOf = useCallback(
    (featureKey) => (pricing[featureKey] ?? 0),
    [pricing]
  );

  /**
   * formatCostLabel — single source of truth for the inline price tag.
   * Returns ' · 10 cr' (leading separator included) or '' for free actions
   * or while pricing is still loading. Never throws.
   */
  const formatCostLabel = useCallback(
    (featureKey) => {
      // Gated behind force_credit_charging — unlimited users get no cost
      // tag on CTAs, matching the hidden CreditsChip.
      if (isUnlimited) return '';
      if (!featureKey || !pricing || Object.keys(pricing).length === 0) return '';
      const cost = pricing[featureKey];
      if (typeof cost !== 'number' || cost <= 0) return '';
      return ` · ${cost.toLocaleString()} cr`;
    },
    [pricing, isUnlimited]
  );

  const openBuyModal = useCallback(
    (detail = null) => setModalState({ open: true, detail }),
    []
  );
  const closeBuyModal = useCallback(
    () => setModalState({ open: false, detail: null }),
    []
  );

  return (
    <CreditsContext.Provider
      value={{
        balance,
        pricing,
        isUnlimited,
        priceOf,
        formatCostLabel,
        refresh,
        openBuyModal,
        closeBuyModal,
        modalState,
      }}
    >
      {children}
    </CreditsContext.Provider>
  );
}
